import {
    Card,
    CardActions,
    FormLabel,
    InputAdornment,
    OutlinedInput,
    TextField,
    Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import styled from "styled-components";
import { ErrorRecord, OptionsArray, OptionsObject, Product, Color, Employee } from "../../types";
import Divider from "@mui/material/Divider";
import React from "react";
import { ChevronDown, FileText } from "lucide-react";
import { sx, typographySx } from "../../utils/util";
import { DatePicker, LocalizationProvider } from "@mui/lab";
import AdapterDayjs from "@mui/lab/AdapterDayjs";
import dayjs from "dayjs";
type Props = {
    changedProduct: Product;
    manufacturers: OptionsArray;
    manufacturer: OptionsObject | null;
    designers: OptionsArray;
    designer: OptionsObject | null;
    productStatuses: OptionsArray;
    productStatus: OptionsObject | null;
    actions: OptionsArray;
    action: OptionsObject | null;
    materials: OptionsArray;
    material: OptionsObject | null;
    handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleSelectChange: (
        e: React.SyntheticEvent<Element, Event>,
        value: OptionsObject | null,
        targetField: string,
    ) => void;
    error: boolean;
    errors: ErrorRecord<Product>;
    colors: Color[];
    handleProductColorChange: (ids: string[]) => void;
    employee: Employee | undefined;
};

export const EditDetails = (props: Props) => {
    const colorNameMap: Record<string, string> = {
        svart: "Black",
        blå: "Blue",
        grön: "Green",
        röd: "Red",
        gul: "Yellow",
        vit: "White",
        grå: "Gray",
        orange: "Orange",
        brun: "Brown",
        lila: "Purple",
        rosa: "Pink",
    };
    console.log(props.actions);
    return (
        <Container>
            <Card
                variant="outlined"
                sx={{
                    borderRadius: "30px",
                    overflowY: "auto",
                    maxHeight: "80vh",
                    padding: "5px",
                    width: "95%",
                }}
            >
                <StyledCardActions>
                    <IconActionsWrapper>
                        <FileText />{" "}
                        <Typography
                            fontFamily={"'Poppins', sans-serif"}
                            fontSize={18}
                            color="#8c8c8c"
                            fontWeight={600}
                        >
                            Details
                        </Typography>
                    </IconActionsWrapper>
                </StyledCardActions>
                <Form noValidate autoComplete="off">
                    <TextField
                        label="Name"
                        name="name"
                        variant="outlined"
                        required
                        onChange={e => props.handleChange(e)}
                        value={props.changedProduct.name}
                        error={props.errors.name ? true : false}
                        helperText={props.errors.name}
                        sx={sx}
                    />
                    {props.manufacturers && (
                        <Autocomplete
                            disablePortal
                            isOptionEqualToValue={(option, value) => option.label === value.label}
                            options={props.manufacturers}
                            onChange={(event, value) =>
                                props.handleSelectChange(event, value, "manufacturer")
                            }
                            renderInput={params => (
                                <TextField
                                    sx={sx}
                                    variant="outlined"
                                    {...params}
                                    label="Supplier"
                                />
                            )}
                            value={props.manufacturer}
                            popupIcon={<ChevronDown size={16} />}
                            sx={sx}
                        />
                    )}
                    {props.designers && (
                        <Autocomplete
                            disablePortal
                            isOptionEqualToValue={(option, value) => option.label === value.label}
                            options={props.designers}
                            onChange={(event, value) =>
                                props.handleSelectChange(event, value, "designer")
                            }
                            renderInput={params => (
                                <TextField
                                    sx={sx}
                                    variant="outlined"
                                    {...params}
                                    label="Designer"
                                />
                            )}
                            value={props.designer}
                            popupIcon={<ChevronDown size={16} />}
                            sx={sx}
                        />
                    )}
                    <Divider />
                    <fieldset
                        style={{
                            width: "100%",
                            height: "100%",
                            paddingLeft: 4,
                            paddingRight: 4,
                            paddingTop: 8,
                            paddingBottom: 8,
                            borderRadius: 4,
                            border: "1px solid #D4D4D4",
                            display: "inline-flex",
                            flexWrap: "wrap",
                            gap: 8,
                            alignItems: "center",
                        }}
                    >
                        <legend
                            style={{
                                position: "absolute",
                                width: "1px",
                                height: "1px",
                                padding: 0,
                                margin: "-1px",
                                overflow: "hidden",
                                clip: "rect(0, 0, 0, 0)",
                                whiteSpace: "nowrap",
                                border: 0,
                            }}
                        >
                            Color selector, multiple choice
                        </legend>

                        {props.colors.map(colorObj => {
                            const isSelected = props.changedProduct.colors?.includes(colorObj.id);
                            const englishName =
                                colorNameMap[colorObj.name?.toLowerCase()] || colorObj.name;

                            return (
                                <button
                                    key={colorObj.id}
                                    type="button"
                                    aria-pressed={isSelected}
                                    aria-label={`Color ${englishName}`}
                                    onClick={() => {
                                        const currentColors = props.changedProduct.colors || [];
                                        let updatedColors;

                                        if (isSelected) {
                                            updatedColors = currentColors.filter(
                                                c => c !== colorObj.id,
                                            );
                                        } else {
                                            updatedColors = [...currentColors, colorObj.id];
                                        }

                                        props.handleProductColorChange(updatedColors);
                                    }}
                                    style={{
                                        width: 48,
                                        height: 48,
                                        background: colorObj.hex,
                                        border: isSelected ? "3px solid black" : "1px solid #ccc",
                                        cursor: "pointer",
                                    }}
                                />
                            );
                        })}
                    </fieldset>

                    {props.materials && (
                        <Autocomplete
                            disablePortal
                            isOptionEqualToValue={(option, value) => option.label === value.label}
                            options={props.materials}
                            onChange={(event, value) =>
                                props.handleSelectChange(event, value, "material")
                            }
                            getOptionLabel={option => {
                                if (!option) return "";

                                switch (props.employee?.language) {
                                    case "sv":
                                        return option.name || "";
                                    case "en":
                                        return option.nameEN || "";
                                    case "no":
                                        return option.nameNO || "";
                                    case "dk":
                                        return option.nameDK || "";
                                    case "fi":
                                        return option.nameFI || "";
                                    default:
                                        return option.nameEN || option.name || "";
                                }
                            }}
                            renderInput={params => (
                                <TextField
                                    sx={sx}
                                    variant="outlined"
                                    {...params}
                                    label="Material"
                                />
                            )}
                            value={props.material}
                            popupIcon={<ChevronDown size={16} />}
                            sx={sx}
                        />
                    )}
                    <Typography
                        fontFamily={"'Poppins', sans-serif"}
                        fontSize={18}
                        color="#8c8c8c"
                        fontWeight={200}
                    >
                        Measurements
                    </Typography>
                    <Divider />
                    <OutlinedInput
                        value={props.changedProduct.height}
                        name="height"
                        onChange={e => props.handleChange(e)}
                        placeholder="Height"
                        type="number"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        sx={sx}
                        endAdornment={
                            <InputAdornment position="end">
                                <Typography
                                    style={{
                                        fontSize: "16px",
                                        fontFamily: "'Yllw Sans a1 241219', sans-serif",
                                        fontWeight: "400",
                                        color: "gray",
                                    }}
                                >
                                    mm
                                </Typography>
                            </InputAdornment>
                        }
                    />
                    <OutlinedInput
                        placeholder="Width"
                        name="width"
                        value={props.changedProduct.width}
                        onChange={e => props.handleChange(e)}
                        type="number"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        sx={sx}
                        endAdornment={
                            <InputAdornment position="end">
                                <Typography style={typographySx}>mm</Typography>
                            </InputAdornment>
                        }
                    />
                    <OutlinedInput
                        placeholder="Depth"
                        name="depth"
                        value={props.changedProduct.depth}
                        onChange={e => props.handleChange(e)}
                        type="number"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        sx={sx}
                        endAdornment={
                            <InputAdornment position="end">
                                <Typography style={typographySx}>mm</Typography>
                            </InputAdornment>
                        }
                    />
                    <OutlinedInput
                        placeholder="Length"
                        name="length"
                        value={props.changedProduct.length}
                        onChange={e => props.handleChange(e)}
                        type="number"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        sx={sx}
                        endAdornment={
                            <InputAdornment position="end">
                                <Typography style={typographySx}>mm</Typography>
                            </InputAdornment>
                        }
                    />
                    <OutlinedInput
                        placeholder="Seating Height"
                        name="seatHeight"
                        value={props.changedProduct.seatHeight}
                        onChange={e => props.handleChange(e)}
                        type="number"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        sx={sx}
                        endAdornment={
                            <InputAdornment position="end">
                                <Typography style={typographySx}>mm</Typography>
                            </InputAdornment>
                        }
                    />
                    <Divider />
                    {props.productStatuses && (
                        <Autocomplete
                            disablePortal
                            isOptionEqualToValue={(option, value) => option.label === value.label}
                            options={props.productStatuses}
                            onChange={(event, value) =>
                                props.handleSelectChange(event, value, "status")
                            }
                            popupIcon={<ChevronDown size={16} />}
                            renderInput={params => (
                                <TextField variant="outlined" {...params} label="Status" sx={sx} />
                            )}
                            // getOptionLabel={option => option.label?.toString() || option.label}
                            getOptionLabel={option => {
                                if (!option) return "";

                                switch (props.employee?.language) {
                                    case "sv":
                                        return option.nameSV || "";
                                    case "en":
                                        return option.name || "";
                                    case "no":
                                        return option.nameNO || "";
                                    case "dk":
                                        return option.nameDK || "";
                                    case "fi":
                                        return option.nameFI || "";
                                    default:
                                        return option.name || "";
                                }
                            }}
                            value={props.productStatus}
                        />
                    )}
                    {props.actions && (
                        <Autocomplete
                            disablePortal
                            isOptionEqualToValue={(option, value) => option.label === value.label}
                            options={props.actions}
                            onChange={(event, value) =>
                                props.handleSelectChange(event, value, "action")
                            }
                            popupIcon={<ChevronDown size={16} />}
                            renderInput={params => (
                                <TextField variant="outlined" {...params} label="Action" sx={sx} />
                            )}
                            value={props.action}
                            getOptionLabel={option => {
                                if (!option) return "";

                                switch (props.employee?.language) {
                                    case "sv":
                                        return option.nameSV || "";
                                    case "en":
                                        return option.name || "";
                                    case "no":
                                        return option.nameNO || "";
                                    case "dk":
                                        return option.nameDK || "";
                                    case "fi":
                                        return option.nameFI || "";
                                    default:
                                        return option.name || "";
                                }
                            }}
                        />
                    )}
                    <Divider />
                    <TextField
                        label="Address"
                        name="address"
                        variant="outlined"
                        onChange={e => props.handleChange(e)}
                        value={props.changedProduct.address}
                        error={props.errors.address ? true : false}
                        helperText={props.errors.name}
                        sx={sx}
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Available from date"
                            value={
                                props.changedProduct.avalible
                                    ? dayjs(props.changedProduct.avalible)
                                    : null
                            }
                            onChange={newValue => {
                                props.handleChange({
                                    target: {
                                        name: "avalible",
                                        value: newValue ? dayjs(newValue).format("YYYY-MM-DD") : "",
                                    },
                                } as React.ChangeEvent<HTMLInputElement>);
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    sx={sx}
                                    error={!!props.errors.avalible}
                                    helperText={props.errors.avalible}
                                />
                            )}
                        />
                    </LocalizationProvider>
                    {/* <TextField
                        label="Avalible"
                        name="avalible"
                        variant="outlined"
                        placeholder="YYYY-MM-DD"
                        onChange={e => props.handleChange(e)}
                        value={props.changedProduct.avalible}
                        error={props.errors.avalible ? true : false}
                        helperText={props.errors.avalible}
                        sx={sx}
                    /> */}
                    <TextField
                        label="Comment"
                        name="comment"
                        variant="outlined"
                        value={props.changedProduct.comment}
                        onChange={e => props.handleChange(e)}
                        sx={sx}
                    />
                </Form>
            </Card>
        </Container>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    align-content: center;
    width: 95%;
    margin: 0 auto;
    gap: 20px;
`;
const IconActionsWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: center;
    gap: 5px;
`;
const StyledCardActions = styled(CardActions)`
    display: flex;
    width: 100%;
    font-size: 3rem;
    border-bottom: 1px solid #ebebeb;
    //background-color: #ebebeb;
    align-items: center;
`;
const Container = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    padding: 0 10px;
`;
